import { AchApprovalOption, LocApprovalOption, TermApprovalOption } from 'ng2-app/models/dto/approval/approval-option.model';
import { Calculator, configureCalculator } from '@lendiodevs/lendio-js-utilities';
import * as ApprovalCalculatorUtility from '@lendiodevs/lendio-js-utilities/lib/approval/ApprovalCalculator.utility.js';
import * as locCalculatorGeneralFormulas from '@lendiodevs/lendio-js-utilities/dist/formulas/general-formulas';
import { Deal, DealsTermsValues } from 'ng2-app/models/dto/deal.model';
import { Offer } from 'ng2-app/services/offers/offer.model';
import { Approval } from 'ng2-app/models/dto/approval/approval.model';
import { get } from 'lodash';
import { DealOption } from 'ng2-app/models/dto/deal-option.model';

export const dealStageColorClassMap = {
  application: '!tw-bg-lendio-cool-gray-700',
  underwriting: '!tw-bg-lendio-blue-700',
  closing: '!tw-bg-lendio-teal-600',
  funded: '!tw-bg-lendio-purple-300',
  inactive: '!tw-bg-lendio-warm-gray-500',
};

export class DealUtility {

  static offerDealStatuses = [
    'offerReceived',
    'offerToBorrower',
    'offerDeclined',
  ];

  static paymentFrequencyMap = {
    'daily': 1,
    'weekly': 7,
    'semiMonthly': 14,
    'monthly': 30,
  };

  static offerPaymentFrequencyMap = {
    'daily': 1,
    'semiweekly': 4,
    'weekly': 7,
    'biweekly': 14,
    'semimonthly': 15,
    'monthly': 30,
    'bimonthly': 60,
  };

  static termTypeMap = {
    'days': 1,
    'weeks': 7,
    'months': 30,
  };

  static calculateApprovalMaxValues(approval): DealsTermsValues {
    //Approval Vars
    const options: AchApprovalOption[] = approval.options;

    const { buyRate = undefined } = approval.calculatorConfig || {};
    // configure calculator
    const calculator: Calculator = configureCalculator({ buyRate });

    const maxTerm = options.reduce((max, o) => o.term > max ? o.term : max, 0);
    const minTerm = options.reduce((min, o) => o.term < min ? o.term : min, 9999999999999);

    const maxPeriod = options
      .filter(option => option.term === maxTerm)
      .reduce((max, o) =>
        DealUtility.paymentFrequencyMap[o.paymentFrequency] > DealUtility.paymentFrequencyMap[max] ? o.paymentFrequency : max, 'daily'
      );

    const maxOption = options.find(o => o.term === maxTerm && o.paymentFrequency === maxPeriod);
    if (!maxOption) {
      return {} as DealsTermsValues;
    }
    const maxOptionTerm = maxOption.term;
    const maxOptionFreq = maxOption.paymentFrequency;
    const maxOptionMaxAmount = maxOption.limits?.amount?.max || 0;
    const maxOptionMaxPoints = maxOption.limits?.points?.max || 0;
    const explicitOriginationMax = maxOption.limits?.originationAmount?.max;
    const maxOriginationPercent = maxOption.limits?.originationPercent?.max ?
      maxOption.limits?.originationPercent?.max :
      explicitOriginationMax ?
        ApprovalCalculatorUtility.toFixedNumber(explicitOriginationMax / maxOptionMaxAmount, 2, 10) :
        0;


    const maxBuyrateConditional = maxOption.customCalculatorParams?.pointConditionalBuyrateThreshold;
    const maxBuyRate = (maxOption !== undefined && maxOptionMaxPoints !== undefined) ?
      calculator.buyRate(maxOption.buyRate, maxOptionMaxPoints, {pointConditionalBuyrateThreshold: maxBuyrateConditional}) : 0;

    const calculationType = approval.calculationType;
    const roundingStrategy = approval.loanProductRoundingStrategy;

    const pointsTradable = approval.pointsTradable;
    const originationPercent = approval.originationPercent;
    const factor = ApprovalCalculatorUtility.getFactorRate({
      pointsTradable: pointsTradable,
      buyRate: maxBuyRate,
      points: maxOptionMaxPoints,
      maxOriginationPercent: maxOriginationPercent,
      originationPercent: originationPercent,
    });

    const maxCalculatedOptionAmount =
      ApprovalCalculatorUtility.getOptionAvailableAmount({option: maxOption, calculationType, factor: factor, roundingStrategy});

    const amount = maxCalculatedOptionAmount;

    const payback = ApprovalCalculatorUtility.getPaybackAmount({
      factor: factor,
      amount: amount,
    });

    const maxOptionPayback  = payback;
    const maxOptionFactor = factor;

    const centsOnDollar = maxOptionPayback / Number(maxCalculatedOptionAmount) - 1;

    const payments = ApprovalCalculatorUtility.getPaymentsNumber(maxOption.payments);
    let maxPayment;

    if (['loc', 'term'].includes(approval.calculationType)) {
      const rate = ('periodicRate' in maxOption) ? parseFloat(maxOption.periodicRate as string) : 0;
      const periodicRate = locCalculatorGeneralFormulas.toFixedNumber(rate || 0, 10);
      maxPayment = locCalculatorGeneralFormulas.getPaymentAmount({
        drawAmount: maxCalculatedOptionAmount,
        periodicRate: periodicRate,
        paymentCount: payments || 0,
      }) || 0.0;
    } else {
      maxPayment = ApprovalCalculatorUtility.getPaymentAmount({
        payback: maxOptionPayback,
        payments: payments,
      });
    }

    return {
      amount: maxCalculatedOptionAmount,
      payback: maxOptionPayback,
      rate: maxOptionFactor,
      term: maxOptionTerm,
      frequency: maxOptionFreq,
      payment: maxPayment,
      fees: centsOnDollar,
      points: maxOptionMaxPoints,
      source: approval,
      sourceType: 'approval',
    };
  }

  static getHighestApprovalByAmount(approvals: Approval[]) {
    return approvals.map( approval => {
      const amountKey = DealUtility.approvalOptionAmountLimitKey(approval);
      const maxOption = DealUtility.getApprovalMaxOption(approval);
      return {
        ...approval,
        maxOptionAmount: maxOption ? get(maxOption.limits, amountKey)?.max : 0,
      };
    }).slice().sort((a, b) => Number(b.maxOptionAmount) - Number(a.maxOptionAmount))[0];
  }

  static getApprovalMaxOption(approval: Partial<Approval>) {
    const amountKey = DealUtility.approvalOptionAmountLimitKey(approval);
    return approval.options
      ?.slice?.()
      ?.sort((a, b) => Number(get(b, `limits.${amountKey}.max`)) - Number(get(a, `limits.${amountKey}.max`)))[0];
  }

  static approvalOptionAmountLimitKey(approval: Partial<Approval>) {
    return approval.calculationType === 'loc' ? 'drawAmount' : 'amount';
  }

  static getTermsValuesFromOffer(offer: Offer) {
    const rates: any[] = [
      offer.apr,
      offer.factor,
      offer.interestRate
    ];
    const rate = rates.find( val => !!val) || undefined;
    return {
      amount: offer.amount,
      payback: offer.payback,
      rate,
      term: offer.term,
      frequency: offer.paymentFrequency,
      payment: offer.paymentAmount,
      fees: offer.calculatedFields?.centsOnDollar,
      points: offer.points,
      source: offer,
      sourceType: 'offer',
    };
  }

  static getHighestTermsValues(deal: Deal): DealsTermsValues {
    const dealHighestApproval = deal.approvals?.length
      ? DealUtility.getHighestApprovalByAmount(deal.approvals)
      : false;

    const dealHighestOffer = deal.offers?.length ?
      deal.offers.slice().sort((a, b) => Number(b.amount) - Number(a.amount))[0]
      : false;

    const dealApprovalMaxAmount: number = dealHighestApproval ? dealHighestApproval.maxOptionAmount as number : 0,
      dealOfferMaxAmount: number = dealHighestOffer ? dealHighestOffer.amount as number : 0;

    // build terms values from either the highest approval or offer
    const termsValues =
      // highest approval greater than highest offer
      dealApprovalMaxAmount > dealOfferMaxAmount
        ? DealUtility.calculateApprovalMaxValues(dealHighestApproval)
      : dealHighestOffer
          ? DealUtility.getTermsValuesFromOffer(dealHighestOffer)
        : {};

    return termsValues as DealsTermsValues;
  }

  static getLastApplicationDate(deal: Deal) {
    let date = deal.lastApplicationDate;
    deal.applications.forEach( app => {
      if(!date || app.created > date) {
        date = app.created;
      }
    });
    return date;
  }

  static sortPublishedOptions(requestedAmount: number, a: DealOption, b: DealOption) {
    const getAmount = (dealOption: DealOption): number => {
      if (dealOption.type === 'option') {
        const option = dealOption.option;
        return parseFloat((option.termSet?.amount || option.termSet?.initialDrawAmount || '').toString()) || 0;
      }
      if (dealOption.type === 'approval') {
        const maxApprovalOption = dealOption.approvalMaxValues;
        return parseFloat((maxApprovalOption?.amount || '').toString()) || 0;
      }
      const offer = dealOption.offer;
      return parseFloat(offer.amount as string) || 0;
    };

    const getCreatedTime = (dealOption: DealOption): Date => {
      const option = dealOption[dealOption.type];
      return new Date(option.created);
    };

    const getPayback = (dealOption: DealOption) => {
      const option = dealOption.type === 'approval' ? dealOption.approvalMaxValues : dealOption[dealOption.type];
      if (!option) {
        return null;
      }

      let payback;
      let factorRate;
      if ('termSet' in option) {
        payback = parseFloat((option.termSet.payback ?? '').toString()) || null;
        factorRate = parseFloat((option.termSet.factorRate ?? '').toString()) || null;
      } else {
        payback = parseFloat(option.payback as string) || null;

        if ('factor' in option) {
          factorRate = parseFloat(option.factor as string) || null;
        }
        if ('rate' in option && dealOption.type === 'approval') {
          factorRate = parseFloat(option.rate.toString()) || null;
        }
      }

      if (payback) {
        return payback;
      }
      // calculate payback only for valid factor rate
      if (factorRate < 1 && factorRate >= 2) {
        return null;
      }

      const amount = getAmount(dealOption);
      if (!amount) {
        return null;
      }
      return amount * factorRate;
    };

    const firstAmount = getAmount(a);
    const secondAmount = getAmount(b);

    if (firstAmount === secondAmount || (!firstAmount && !secondAmount)) {
      const typeOrder = ['loc', 'term', 'ach'].reverse();
      const firstLoanTypePriority = typeOrder.indexOf(a?.deal?.loan_product?.type ?? '') + 1;
      const secondLoanTypePriority = typeOrder.indexOf(b?.deal?.loan_product?.type ?? '') + 1;

      if (firstLoanTypePriority !== secondLoanTypePriority) {
        return secondLoanTypePriority - firstLoanTypePriority;
      }

      if (!firstAmount && !secondAmount) {
        return getCreatedTime(a).valueOf() - getCreatedTime(b).valueOf();
      }

      const firstPayback = getPayback(a);
      const secondPayback = getPayback(b);

      if (!firstPayback && !secondPayback) {
        return getCreatedTime(a).valueOf() - getCreatedTime(b).valueOf();
      }

      if (!firstPayback) {
        return 1;
      } else if (!secondPayback) {
        return -1;
      }

      const firstCostOfCapital = firstPayback - firstAmount;
      const secondCostOfCapital = secondPayback - secondAmount;

      return firstCostOfCapital - secondCostOfCapital;
    }

    if (!firstAmount) {
      return 1;
    } else if (!secondAmount) {
      return -1;
    }

    // sort from max to min amount if no requested
    if (!requestedAmount) {
      return secondAmount - firstAmount;
    }

    // sort from min to max to meet closest requested amount
    if (firstAmount >= requestedAmount && secondAmount >= requestedAmount) {
      return firstAmount - secondAmount;
    }

    if (firstAmount >= requestedAmount) {
      return -1;
    } else if (secondAmount >= requestedAmount) {
      return 1;
    }

    // sort from max to min amount if loan amount less than requested
    return secondAmount - firstAmount;
  }
}
